import * as React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
import { matchPath } from 'react-router'
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
// store
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
// hooks
import { useRiskTemplateAPI } from 'hooks/risks/useRiskTemplateAPI'
import { useRiskRelatedForms } from 'hooks/risks'
import { useLogicalConditions } from 'hooks/risks/useLogicalConditions'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
// graphql
import { RISK_TEMPLATE_QUERY, RISK_TEMPLATE_PB_QUERY, LINKED_RISK_TEMPLATES_QUERY } from 'graphql/risk/queries'
// types
import { TRefetch } from 'types/refetch'
import { NetworkStatus } from '@apollo/client'
import {
  IField,
  IRiskView,
  IFieldLink,
  IRiskTemplate,
  IFieldLinkInput,
  IFieldValueInput,
  IRiskTemplatesQueryArgs,
  IRiskTemplateUpdateInput,
  IFieldUpdatePositionInput
} from 'types/graphql'
// utils
import { get } from 'lodash'
import { delay } from 'utils/helpers'
import { filterByRiskView } from 'utils/form'

export declare namespace NRiskTemplateEditAPIContext {
  export interface IContext {
    isRisk: boolean
    isLoading: boolean
    riskTemplate: IRiskTemplate
    isRiskTemplate: boolean
    isRisksRegister: boolean
    basicFields: IField[]
    linkedRiskTemplates: IRiskTemplate[]
    // methods
    reorderFields: (
      fieldsOrderInput: IFieldUpdatePositionInput[],
      fieldLinksOrderInput?: IFieldLinkInput[]
    ) => Promise<void>
    deleteForm: (fieldLink: IFieldLink) => Promise<void>
    createField: (field: IField) => Promise<IField>
    updateField: (data: IField, field: IField) => Promise<void>
    deleteField: (field: IField) => Promise<void>
    refetchTemplate: TRefetch<any, IRiskTemplatesQueryArgs>
    hasInactiveFields: boolean
    deleteFieldValues: (ids: string[]) => Promise<void>
    updateFieldValues: (values: IFieldValueInput[], entityId?: string, field?: IField) => Promise<any>
    updateRiskTemplate: (input: IRiskTemplateUpdateInput) => Promise<void>
  }

  interface IProps {
    riskView?: IRiskView
    children?: React.ReactNode
    riskTemplateId?: string
  }
}

export const RiskTemplateAPIContext = React.createContext<NRiskTemplateEditAPIContext.IContext>({} as any)

const RiskTemplateAPI: React.FC<NRiskTemplateEditAPIContext.IProps> = ({ children, riskView, riskTemplateId }) => {
  const router = useRouter()

  const isPublic = React.useContext(PublicContext)
  const isRiskTemplate = !riskView && !!matchPath('/forms/templates/edit/:id', router.pathname)
  const isRisksRegister = !!riskView || router.pathname === '/forms'
  const isRisk = !isRisksRegister && !isRiskTemplate

  const { risk, isLoading: isRiskLoading } = React.useContext(RiskAPIContext)

  const isTempId = (router.query.id as string)?.startsWith('temp_')

  const {
    data: riskTemplateQuery,
    networkStatus,
    refetch
  } = useQuery(isPublic ? RISK_TEMPLATE_PB_QUERY : RISK_TEMPLATE_QUERY, {
    fetchPolicy: 'network-only',
    // The template is fetched with the record's id, so wait while the record is still on its way.
    // `isRiskLoading` is only ever true where RiskAPI actually queries a record, which is why no
    // route check is needed here.
    skip: isTempId || (isRiskLoading && !risk),
    variables: {
      filter: {
        ids: [riskTemplateId || (isRiskTemplate ? router.query.id : router.query.templateId)],
        riskId: risk?.id
      },
      ...(isPublic && { domain: router.query.domain })
    }
  })

  const linkedTemplates: IFieldLink[] = get(
    riskTemplateQuery,
    `${isPublic ? 'riskTemplatesPublic' : 'riskTemplates'}.data[0].fieldLinks`,
    []
  )

  const { data: linkedTemplatesQuery } = useQuery(LINKED_RISK_TEMPLATES_QUERY, {
    skip: isPublic || !linkedTemplates.length,
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ids: linkedTemplates.filter(({ primary }) => primary).map(({ field: { entityId } }) => entityId)
      }
    }
  })

  const { parentRelatedForm } = useRiskRelatedForms()

  const riskTemplates = useSelector(riskTemplatesSelector)

  const isLoading = [NetworkStatus.loading, NetworkStatus.setVariables].includes(networkStatus)
  const isRelatedForm = parentRelatedForm && !riskView

  const offlineRiskTemplate = riskTemplates.find((item) => item.id === (riskTemplateId || router.query.templateId))

  const riskTemplate: IRiskTemplate = isRelatedForm
    ? parentRelatedForm.riskTemplate
    : (get(riskTemplateQuery, `${isPublic ? 'riskTemplatesPublic' : 'riskTemplates'}.data[0]`) ?? offlineRiskTemplate)

  const linkedRiskTemplates: IRiskTemplate[] = get(linkedTemplatesQuery, 'riskTemplates.data', [])
  const fields: IField[] = get(riskTemplate, 'fields', [])

  const showIsPublicOnly = isRiskTemplate || riskView || risk?.isCreatedFromGuestAccess

  const filteredFields = router.query.archived
    ? fields
        .filter(({ active }) => !active)
        .map((item) => ({
          ...item,
          parentFieldId: fields.some(({ id, active }) => id === item.parentFieldId && active)
            ? null
            : item.parentFieldId
        }))
    : isPublic
      ? fields.filter(({ active, hideOnPublic }) => active && !hideOnPublic)
      : fields.filter(({ active, isPublicOnly }) => active && (showIsPublicOnly ? true : !isPublicOnly))

  const visibleFields = useLogicalConditions({
    isRisk,
    fields: filteredFields,
    formConditions: riskTemplate?.formConditions
  })

  const fieldLinksFields =
    riskTemplate?.fieldLinks?.map((item: IFieldLink) => ({
      ...item.field,
      parentFieldId: item.parentFieldId,
      position: item.position,
      label: item.fieldLabel,
      fieldLinkId: item.id,
      primary: item.primary
    })) ?? []

  const resolvedFields: IField[] = [...visibleFields, ...fieldLinksFields].filter((field) =>
    filterByRiskView(field, riskView)
  )

  const fieldValueMap = get(risk || riskTemplate, 'fieldValueMap', []).filter(({ fieldId }) =>
    resolvedFields.some(({ id }) => id === fieldId)
  )

  const hasInactiveFields = fields.filter(({ active }) => !active).length > 0

  React.useEffect(() => {
    const handleOnline = async () => {
      await delay(1000)
      refetch()
    }

    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  const {
    deleteForm,
    createField,
    updateField,
    deleteField,
    reorderFields,
    updateFieldValues,
    deleteFieldValues,
    updateRiskTemplate
  } = useRiskTemplateAPI({ riskTemplate, refetch, fields: resolvedFields })

  const value = {
    isRisk,
    isLoading,
    isRiskTemplate,
    isRisksRegister,
    riskTemplate: {
      ...riskTemplate,
      fieldValueMap,
      fields: resolvedFields
    },
    basicFields: filteredFields,
    hasInactiveFields,
    linkedRiskTemplates,
    // methods
    deleteForm,
    createField,
    updateField,
    deleteField,
    reorderFields,
    updateFieldValues,
    deleteFieldValues,
    updateRiskTemplate,
    refetchTemplate: refetch
  }

  return <RiskTemplateAPIContext.Provider value={value}>{children}</RiskTemplateAPIContext.Provider>
}

export default RiskTemplateAPI
